import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { InputField } from 'lib/iterable-hook-form';
import { SelectV2Field } from 'lib/iterable-hook-form/FormField/SelectV2Field';
import { UserActionsMetadata } from 'modules/campaigns/lib';
import { CATEGORY_LIMIT } from 'modules/dragAndDropEditor/constants';
import {
  DragAndDropEditorModalType,
  useDragAndDropEditorModalsContext,
} from 'modules/dragAndDropEditor/context';
import { useDragAndDropEditorContext } from 'modules/dragAndDropEditor/context/DragAndDropEditorContext';
import { SavedRow } from 'modules/dragAndDropEditor/types';
import { getCategoryOptions } from 'modules/dragAndDropEditor/utils';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types/GraphQLTypeNameData';
import { FormField } from 'ui/formField';
import { Link } from 'ui/links';
import { BaseOption } from 'ui/selectv2/types';
import { body2, caption, colors, spacing } from 'ui/theme';
import { SavedRowPreview } from '../SavedRowPreview';
import {
  CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES,
  CreateOrEditSavedRowFormValues,
} from './CreateOrEditSavedRowFormProvider';

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
`;

const SecondaryBodyText = styled.span`
  ${body2};
  color: ${colors.textSecondary};
`;

const FormFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

const Caption = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

export const CATEGORY_PLACEHOLDER = 'Select or add a new category';
const UNIQUE_ROW_NAME_ERROR_MESSAGE = 'Row name must be unique';

export const SAVED_ROW_BODY_ID = 'saved-row-body';
export const ROW_NAME_ID = 'row-name';
export const CATEGORY_ID = 'category';

const DRAG_AND_DROP_EDITOR_HELP_CENTER_LINK =
  'https://support.iterable.com/hc/en-us/articles/11555162439956-Drag-and-Drop-Editor';

type CreateOrEditSavedRowSlideOverBodyProps = {
  savedRow?: SavedRow;
  isEditingRow: boolean;
  handleClose: () => void;
  onRequestClose: (close: () => void) => void;
};

export const CreateOrEditSavedRowSlideOverBody = ({
  savedRow,
  isEditingRow,
  handleClose,
  onRequestClose,
}: CreateOrEditSavedRowSlideOverBodyProps) => {
  const [newOptions, setNewOptions] = useState<BaseOption[]>([]);

  const {
    createSavedRowFailure,
    updateSavedRowFailure,
    resetUpdateSavedRow,
    createSavedRowReset,
    messageCategories,
    parseSavedRowProcess,
    hasUnsavedTemplateChanges,
    setIsSavingTemplateForSavedRow,
    editSavedRowDesign: {
      saveTemplateForSavedRow,
      startEditSavedRowDesign,
      onResetDragAndDropTemplateChanges,
    },
  } = useDragAndDropEditorContext();
  const { openModal, closeModal } = useDragAndDropEditorModalsContext();

  const {
    watch,
    formState: { errors },
  } = useFormContext<CreateOrEditSavedRowFormValues>();

  const hasUniqueRowNameError =
    createSavedRowFailure?.__typename === GRAPHQL_TYPENAMES.BEE_ROW_NAME_ALREADY_EXISTS_FAILURE ||
    updateSavedRowFailure?.__typename === GRAPHQL_TYPENAMES.BEE_ROW_NAME_ALREADY_EXISTS_FAILURE ||
    updateSavedRowFailure?.errorMsg === 'Duplicate row name key';

  const options = getCategoryOptions(messageCategories);

  const rowNameErrorMessage = errors?.rowName?.message;
  const categoryNameErrorMessage = errors?.category?.label?.message;

  useEffect(() => {
    const { unsubscribe } = watch((_, { name }) => {
      if (name === CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.ROW_NAME && hasUniqueRowNameError) {
        if (isEditingRow) {
          resetUpdateSavedRow();
        } else {
          createSavedRowReset();
        }
      }
    });

    return () => unsubscribe();
  }, [hasUniqueRowNameError, isEditingRow, resetUpdateSavedRow, createSavedRowReset, watch]);

  const isLoadingPreview = !isEditingRow && !!parseSavedRowProcess?.isLoading;
  const html =
    (isEditingRow ? savedRow?.primaryContent?.html : parseSavedRowProcess?.data?.html) || ''; // `savedRow` is only available when editing an existing row

  const handleEditSavedRow = () => {
    const onCloseSavedRowUnsavedTemplateChangesWarning = () =>
      closeModal(DragAndDropEditorModalType.SavedRowUnsavedTemplateChangesWarning);

    const onContinue = () => {
      onCloseSavedRowUnsavedTemplateChangesWarning();
      handleClose();
      setIsSavingTemplateForSavedRow(false);
      startEditSavedRowDesign(savedRow as SavedRow);
    };

    onRequestClose(() => {
      if (hasUnsavedTemplateChanges) {
        openModal(DragAndDropEditorModalType.SavedRowUnsavedTemplateChangesWarning, {
          onClose: onCloseSavedRowUnsavedTemplateChangesWarning,
          onContinueWithoutSaving: () => {
            onResetDragAndDropTemplateChanges(); // clears unsaved template changes
            onContinue();
          },
          onSaveAndContinue: () => {
            setIsSavingTemplateForSavedRow(true);
            saveTemplateForSavedRow(onContinue, () => setIsSavingTemplateForSavedRow(false));
          },
        });
      } else {
        onContinue();
      }
    });
  };

  return (
    <BodyContainer data-test={SAVED_ROW_BODY_ID}>
      {!isEditingRow && (
        <SecondaryBodyText>
          Saved content to reuse later. When you use and edit this row, edits won’t affect other
          designs using the same row.{' '}
          <Link target="_blank" href={DRAG_AND_DROP_EDITOR_HELP_CENTER_LINK}>
            Learn more
          </Link>
        </SecondaryBodyText>
      )}

      {/* Image preview */}
      <SavedRowPreview
        isEditing={isEditingRow}
        isLoading={isLoadingPreview}
        html={html}
        onEdit={handleEditSavedRow}
      />

      {/* Row name field */}
      <FormField
        errorText={hasUniqueRowNameError ? UNIQUE_ROW_NAME_ERROR_MESSAGE : rowNameErrorMessage}
        hasError={!!rowNameErrorMessage || hasUniqueRowNameError}
        labelText="Row name"
      >
        <InputField
          data-test={ROW_NAME_ID}
          id="row-name"
          name={CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.ROW_NAME}
        />
      </FormField>

      {/* Category field */}
      <FormFieldWrapper>
        <FormField
          data-test="row-category-field"
          errorText={categoryNameErrorMessage}
          hasError={!!categoryNameErrorMessage}
          labelText={
            <span>
              Category <SecondaryBodyText>(optional)</SecondaryBodyText>
            </span>
          }
        >
          <SelectV2Field
            canCreate
            createSuffix="category"
            id={CATEGORY_ID}
            isClearable
            isOptionDisabled={(option) =>
              !!option.tooltipContent || !!(option?.__isNew__ && options?.length >= CATEGORY_LIMIT)
            }
            maxMenuHeight={300}
            menuPortalTarget={document.body}
            name={CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.CATEGORY}
            onCreateOption={(val) => setNewOptions((opts) => [{ label: val, value: val }, ...opts])}
            options={[...newOptions, ...options]}
            placeholder={CATEGORY_PLACEHOLDER}
          />
        </FormField>
        <Caption>
          Add your row to a category to make it easier to find and manage.{' '}
          <Link target="_blank" href={DRAG_AND_DROP_EDITOR_HELP_CENTER_LINK} size="caption">
            Learn more
          </Link>
        </Caption>
      </FormFieldWrapper>
      {/* Only display when editing existing rows */}
      {isEditingRow && savedRow && (
        <UserActionsMetadata
          bordered
          variant="compact"
          creator={savedRow.creatorUser}
          createdDate={savedRow.createdAt}
          modifiedDate={savedRow.updatedAt}
          modifier={savedRow.updatedByUser}
          backgroundColor={colors.backgroundTertiary}
        />
      )}
    </BodyContainer>
  );
};
